import { useEffect, useState } from 'react';
import { workspaceApi } from '@/lib/api/workspace';
import type { WorkspaceMember } from 'dico-types';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { Icons } from '@/lib/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface MembersSettingsPageProps {
  workspaceId: string;
}

function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  const initials = parts
    .map((p) => p[0])
    .join('')
    .toUpperCase();
  if (initials.length >= 2) return initials.slice(0, 2);
  return trimmed.slice(0, 2).toUpperCase();
}

export function MembersSettingsPage({ workspaceId }: MembersSettingsPageProps) {
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    workspaceApi.getWorkspaceMembers(workspaceId).then((res) => {
      if (cancelled) return;
      setMembers(res.data ?? []);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  return (
    <div className="w-3xl mx-auto min-h-full flex-1 space-y-10 px-8 py-10 xl:border-x">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Members</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Manage your workspace details and membership.
        </p>
      </div>

      <Separator />

      {/* Users section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Users</span>
            {!isLoading && (
              <span className="text-muted-foreground bg-muted rounded-full px-2 py-0.5 text-xs font-medium">
                {members.length}
              </span>
            )}
          </div>
          <Button size="sm" variant="outline" className="flex items-center gap-1.5">
            <MonoIcon type={Icons.Plus} className="h-3.5 w-3.5" />
            Invite user
          </Button>
        </div>

        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                  Name
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                  Joined
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                  Role
                </th>
                <th className="w-10 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="text-muted-foreground p-4 text-center text-sm">
                    Loading members…
                  </td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-muted-foreground p-4 text-center text-sm">
                    No members yet.
                  </td>
                </tr>
              ) : (
                members.map((member) => <MemberRow key={member.id} member={member} />)
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invitations section — placeholder until invite API is available */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Invitations</span>
          <span className="text-muted-foreground bg-muted rounded-full px-2 py-0.5 text-xs font-medium">
            0
          </span>
        </div>

        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                  Email address
                </th>
                <th className="text-muted-foreground px-4 py-3 text-left text-xs font-medium">
                  Sent
                </th>
                <th className="w-10 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={3} className="text-muted-foreground px-4 py-8 text-center text-sm">
                  No pending invitations.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MemberRow({ member }: { member: WorkspaceMember }) {
  const displayName = member.displayName?.trim() ? member.displayName : member.userId;
  const initials = getInitials(displayName);

  return (
    <tr className="border-b last:border-b-0">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar size="sm">
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium">{displayName}</span>
        </div>
      </td>
      <td className="text-muted-foreground px-4 py-3 text-sm">
        {formatRelativeTime(member.createdAt)}
      </td>
      <td className="px-4 py-3">
        <RoleBadge role={member.role} />
      </td>
      <td className="px-4 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger className="text-muted-foreground hover:text-foreground hover:bg-muted flex h-7 w-7 items-center justify-center rounded-md transition-colors">
            <MonoIcon type={Icons.MoreHorizontal} className="h-4 w-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem className="text-destructive focus:text-destructive">
              Remove member
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

function RoleBadge({ role }: { role: string }) {
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin';

  return <Badge variant={isOwner || isAdmin ? 'outline' : 'outline'}>{role}</Badge>;
}
